import objectHash from "object-hash";
import { useEffect, useMemo, useState } from "react";
import * as uuid from "uuid";

import { GlobalThread, Thread, ThreadFree } from "./components/Thread";
import { CommentsContext, ICommentsContext } from "./context";
import { IComment, IThreadsGroup } from "./types";
import { getColorByIndex } from "./utils";
import { ISelection } from "../RichEditor/RichEditor";

export * from "./types";

interface IProps {
  isManager: boolean;
  title?: string;
  hidden?: boolean;
  upsertComment: (comment: IComment) => Promise<IComment>;
  deleteComment: (id: string) => Promise<boolean>;
  comments: IComment[];
  children: (ctx: ICommentsContext) => React.ReactNode;
}

function Comments(props: IProps) {
  const filteredComments = useMemo(() => {
    return props.comments.filter(
      (x) =>
        props.isManager ||
        (!props.isManager && (x.from === "client" || !x.isDraft))
    );
  }, [props.comments, props.isManager]);

  const [comments, setComments] = useState<IComment[]>(filteredComments);

  const [context, setContext] = useState<ICommentsContext>({
    threads: {},
    hash: "",
    isManager: false,
    hidden: !!props.hidden,
    comments: [],
  });
  const [showedThreadName, setShowedThreadName] = useState<string>("");
  const [focusedCommentId, setFocusedCommentId] = useState<string>("");

  function buildCommentTree(comments: IComment[]): IComment[] {
    const map = new Map<string, IComment>();
    const roots: IComment[] = [];

    // создаём клоны с новым массивом replies
    const clones = comments.map((c) => ({ ...c, replies: [] as IComment[] }));
    clones.forEach((c) => map.set(c.id, c));

    clones.forEach((c) => {
      if (c.replyId) {
        const parent = map.get(c.replyId);
        if (parent) {
          const replies = parent.replies ?? (parent.replies = []);
          replies.push(c);
        } else {
          roots.push(c);
        }
      } else {
        roots.push(c);
      }
    });

    return roots;
  }

  useEffect(() => {
    setComments(filteredComments);
  }, [filteredComments]);

  useEffect(() => {
    const result: IThreadsGroup = {};

    for (const comment of comments) {
      if (!result[comment.name]) {
        result[comment.name] = [];
      }

      result[comment.name].push(comment);
    }

    for (const name in result) {
      result[name] = buildCommentTree(result[name]);
    }

    setContext({
      hash: objectHash(result),
      hidden: !!props.hidden,
      threads: result,
      isManager: props.isManager,
      async saveComment(id: string, value: string) {
        const result = comments.find((item) => id === item.id);

        if (!result) {
          return;
        }

        if (result.selection) {
          document.dispatchEvent(
            new CustomEvent("text:marker:created", {
              detail: {
                id: result.id,
                color: result.color,
                selection: result.selection,
                name: result.name,
              },
            })
          );
        }

        result.text = value;
        result.isEditable = false;
        result.isNew = false;
        // Не меняем isDraft при сохранении - комментарий остается черновиком
        // result.isDraft = false;
        const updatedComment: IComment = {
          ...result,
          text: value,
          isEditable: false,
          isNew: false,
          replyId: result.replyId,
        };

        await props.upsertComment(updatedComment);

        setComments([...comments]);
      },
      editComment: function (id: string) {
        const newComments = comments.map((item) => ({
          ...item,
          isEditable: false,
          isShowed: false,
        }));
        const result = newComments.find((item) => id === item.id);
        if (result) {
          result.isEditable = true;
        }
        setComments([...newComments]);
      },
      deleteComment: async function (id: string, force = false) {
        const comment = comments.find((item) => item.id === id);
        if (!comment?.isNew && !force) {
          const isDeleted = await props.deleteComment(id);

          if (!isDeleted) {
            return;
          }
        }

        const result = comments.filter((item) => id !== item.id);
        document.dispatchEvent(
          new CustomEvent("text:marker:deleted", {
            detail: {
              id,
              name: comment?.name,
            },
          })
        );
        setComments([...result]);
      },
      addComment: function (
        name: string,
        selection?: ISelection,
        replyId?: string
      ) {
        const currentUserRole = props.isManager ? "manager" : "client";

        // 🔒 Защита: если уже ответили на этот комментарий — не добавляем
        if (replyId) {
          const alreadyReplied = comments.some(
            (comment) =>
              comment.replyId === replyId && comment.from === currentUserRole
          );
          if (alreadyReplied) {
            return; // already responded to this comment
          }
        }

        // 🔄 Очистка предыдущих isNew комментариев
        const newComments = comments
          .filter((comment) => !comment.isNew)
          .map((comment) => ({
            ...comment,
            isEditable: false,
            isShowed: false,
          }));

        const id = uuid.v4();
        const color = selection
          ? getColorByIndex(
              newComments.filter(
                (item) => !!item.selection && item.name === name
              ).length ?? 0
            )
          : "";

        for (const commentWithSelection of comments.filter(
          (item) => !!item.selection && item.isNew
        )) {
          document.dispatchEvent(
            new CustomEvent("text:marker:deleted", {
              detail: {
                id: commentWithSelection?.id,
              },
            })
          );
        }

        newComments.push({
          id,
          name,
          text: "",
          color,
          isEditable: true,
          isNew: true,
          isDraft: true,
          isShowed: false,
          from: currentUserRole,
          replyId: replyId ?? undefined,
          selection,
        });

        setComments([...newComments]);
      },

      async resolveComment(name: string, id: string, state) {
        const unresolvedCommentsCount = comments.filter(
          (item) => item.name === name && !item.isResolved
        ).length;
        const comment = comments.find((item) => item.id === id);

        if (comment) {
          // Close thread if last comment was resolved
          if (unresolvedCommentsCount === 1) {
            setShowedThreadName("");
          }
          comment && (comment.isResolved = state);
          await props.upsertComment(comment);
        }

        setComments([...comments]);
      },
      setFocusedCommentId(id: string) {
        const comment = comments.find((item) => item.id === id);
        if (!comment) {
          return;
        }

        const sameComments = comments.filter(
          (item) => item.name === comment.name
        );

        if (sameComments.length > 1) {
          setShowedThreadName(comment.name);
        }

        setFocusedCommentId(id);
        setTimeout(() => {
          setFocusedCommentId("");
        }, 500);
      },
      showedThreadName,
      setShowedThreadName,
      focusedCommentId,
      comments,
    });
  }, [comments, showedThreadName, focusedCommentId, props]);

  return (
    <CommentsContext.Provider value={context}>
      {props.children(context)}
    </CommentsContext.Provider>
  );
}

Comments.Thread = Thread;
Comments.Content = ThreadFree;
Comments.GlobalThread = GlobalThread;
export default Comments;
